import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Loader2 } from 'lucide-react';
import { redeemBetaInvitation, signInWithIdentifier, signUpWithEmail } from '../../lib/auth';

type Invitation = { valid: boolean; reason?: string; name?: string; email?: string };

function getInviteToken() {
  try {
    const fragment = String(window.location.hash || '').replace(/^#/, '');
    const fromHash = new URLSearchParams(fragment).get('invite') || '';
    if (fromHash) return fromHash;
    return new URLSearchParams(window.location.search).get('invite') || '';
  } catch {
    return '';
  }
}

export default function BetaRegistrationPage() {
  const token = useMemo(getInviteToken, []);
  const [invitation, setInvitation] = useState<Invitation | null>(null);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState<'signup' | 'signin'>('signup');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    if (!token) {
      setInvitation({ valid: false, reason: 'invalid' });
      setLoading(false);
      return () => { cancelled = true; };
    }
    void fetch('/api/beta/invitations/validate', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token }),
    }).then(async (response) => {
      const payload = await response.json().catch(() => ({}));
      if (!cancelled) setInvitation(response.ok ? payload : { valid: false, reason: payload?.error });
    }).catch(() => {
      if (!cancelled) setInvitation({ valid: false, reason: 'unavailable' });
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => { cancelled = true; };
  }, [token]);

  const finishRedemption = async () => {
    await redeemBetaInvitation(token);
    setMessage('Your complimentary beta access is active. Opening your workspace…');
    window.setTimeout(() => { window.location.assign('/dashboard'); }, 250);
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!invitation?.valid) return;
    setSubmitting(true);
    setError('');
    setMessage('');
    try {
      if (mode === 'signin') {
        await signInWithIdentifier(invitation.email || '', password);
        await finishRedemption();
      } else {
        const callback = new URL('/auth/callback', window.location.origin);
        callback.searchParams.set('flow', 'beta-confirmation');
        callback.searchParams.set('invite', token);
        const result = await signUpWithEmail(invitation.email || '', password, username, callback.toString(), token);
        if (result.session) {
          await finishRedemption();
        } else {
          setMessage('Check your email and verify your address to activate complimentary beta access.');
        }
      }
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : 'Unable to complete beta registration.');
    } finally {
      setSubmitting(false);
    }
  };

  const invalidMessage = invitation?.reason === 'expired'
    ? 'This beta invitation has expired. Contact the Trade-Proof team for a new invitation.'
    : invitation?.reason === 'unavailable'
      ? 'We could not check this invitation right now. Please try again.'
      : 'This beta invitation is invalid, already used, or revoked.';

  return (
    <main className="flex min-h-screen items-center justify-center bg-gradient-to-br from-[#0b1220] via-[#0f172a] to-[#020617] p-4 font-sans text-slate-200">
      <div className="absolute top-0 left-0 h-full w-full overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] left-[-10%] h-96 w-96 rounded-full bg-blue-500/10 blur-3xl opacity-30" />
        <div className="absolute bottom-[-10%] right-[-10%] h-96 w-96 rounded-full bg-indigo-500/10 blur-3xl opacity-30" />
      </div>

      <div className="relative w-full max-w-md overflow-hidden rounded-2xl border border-slate-700/50 bg-slate-900/85 p-6 sm:p-8 shadow-2xl backdrop-blur-xl">
        <div className="flex items-center gap-2 mb-3">
          <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-cyan-400">Trade-Proof Beta</div>
        </div>

        <h1 className="text-2xl font-bold tracking-tight text-white sm:text-3xl">
          Welcome to Trade-Proof
        </h1>

        {loading ? (
          <div className="mt-6 flex items-center gap-3 rounded-lg border border-sky-500/30 bg-sky-500/10 p-4 text-sm text-sky-200" aria-live="polite">
            <Loader2 className="h-4 w-4 animate-spin flex-shrink-0" />
            <span>Checking your private invitation…</span>
          </div>
        ) : !invitation?.valid ? (
          <p role="alert" className="mt-5 rounded-lg border border-amber-500/40 bg-amber-500/10 p-4 text-sm text-amber-200 leading-relaxed">
            {invalidMessage}
          </p>
        ) : (
          <>
            <p className="mt-2 text-sm text-slate-400 leading-relaxed">
              Your complimentary beta access is ready. Create your account to get started.
            </p>

            <form className="mt-6 space-y-4" onSubmit={(event) => void submit(event)}>
              {mode === 'signup' ? (
                <div>
                  <label htmlFor="beta-username" className="block text-xs font-semibold uppercase tracking-wider text-slate-300">
                    Username
                  </label>
                  <input
                    id="beta-username"
                    required
                    minLength={3}
                    maxLength={20}
                    pattern="[A-Za-z0-9_-]{3,20}"
                    value={username}
                    onChange={(event) => setUsername(event.target.value)}
                    autoComplete="username"
                    placeholder="Choose a username"
                    className="mt-1.5 block w-full rounded-lg border border-slate-700 bg-slate-950/60 px-3.5 py-2.5 text-sm text-slate-200 placeholder:text-slate-600 focus:border-cyan-500 focus:outline-none focus:ring-1 focus:ring-cyan-500"
                  />
                </div>
              ) : null}

              <div>
                <label htmlFor="beta-email" className="block text-xs font-semibold uppercase tracking-wider text-slate-300">
                  Email
                </label>
                <input
                  id="beta-email"
                  readOnly
                  value={invitation.email || ''}
                  autoComplete="email"
                  className="mt-1.5 block w-full rounded-lg border border-slate-800 bg-slate-950/30 px-3.5 py-2.5 text-sm text-slate-400 cursor-not-allowed select-none"
                />
              </div>

              <div>
                <label htmlFor="beta-password" className="block text-xs font-semibold uppercase tracking-wider text-slate-300">
                  Password
                </label>
                <input
                  id="beta-password"
                  required
                  type="password"
                  minLength={6}
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  autoComplete={mode === 'signin' ? 'current-password' : 'new-password'}
                  placeholder="Enter your password"
                  className="mt-1.5 block w-full rounded-lg border border-slate-700 bg-slate-950/60 px-3.5 py-2.5 text-sm text-slate-200 placeholder:text-slate-600 focus:border-cyan-500 focus:outline-none focus:ring-1 focus:ring-cyan-500"
                />
              </div>

              {error ? (
                <p role="alert" className="rounded-lg border border-rose-500/40 bg-rose-500/10 p-3 text-sm text-rose-200 leading-snug">
                  {error}
                </p>
              ) : null}

              {message ? (
                <p role="status" className="rounded-lg border border-emerald-500/40 bg-emerald-500/10 p-3 text-sm text-emerald-200 leading-snug">
                  {message}
                </p>
              ) : null}

              <button
                type="submit"
                disabled={submitting}
                className="mt-2 w-full rounded-lg bg-cyan-500 px-4 py-2.5 font-semibold text-slate-950 transition-colors hover:bg-cyan-400 active:bg-cyan-600 disabled:cursor-not-allowed disabled:opacity-60 text-sm shadow-md shadow-cyan-950/40"
              >
                {submitting ? 'Please wait…' : mode === 'signin' ? 'Sign in and activate beta access' : 'Activate Free Access'}
              </button>
            </form>

            {!message ? (
              <div className="mt-4 pt-2 text-center">
                <button
                  type="button"
                  onClick={() => { setMode(mode === 'signup' ? 'signin' : 'signup'); setError(''); }}
                  className="text-xs sm:text-sm text-cyan-400 hover:text-cyan-300 underline underline-offset-4 transition-colors"
                >
                  {mode === 'signup' ? 'Already have an account? Sign in' : 'Need an account? Create one'}
                </button>
              </div>
            ) : null}

            <p className="mt-4 text-center text-[11px] text-slate-500">
              Complimentary access is granted only after the invited email is verified.
            </p>
          </>
        )}
      </div>
    </main>
  );
}
